import React from 'react';
import { motion } from 'framer-motion';
interface Stat {
  value: string;
  label: string;
}
interface StripeScreenshot {
  src: string;
  alt: string;
  caption: string;
}
const STATS: Stat[] = [{
  value: '$13.3M',
  label: 'gross volume processed'
}, {
  value: '+279%',
  label: 'gross volume, period over period'
}, {
  value: '21,306',
  label: 'successful payments (+414%)'
}, {
  value: '$210K',
  label: 'MRR (+124%)'
}];
const STRIPE_SCREENSHOTS: StripeScreenshot[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/etRUDyEhomLYjCKeUKa2zd/Case-Studies-17.png',
  alt: 'Stripe dashboard showing $13.3 million gross volume and $12.5 million net volume from January 2020 through January 2026.',
  caption: '$13.3M gross volume · $12.5M net volume'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/svCymABLq42d5ZJPypS6Uz/Case-Studies-14.png',
  alt: 'Stripe overview showing gross volume growth of 279 percent, net volume, and spend per customer.',
  caption: '279% gross-volume growth'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nUREFvvV83zrny3vZPjEoL/Case-Studies-13.png',
  alt: 'Stripe yearly overview showing gross volume, net volume, and spend per customer growth from 2023 through 2025.',
  caption: 'Consistent annual growth'
}];
const FINAL_SCREENSHOT: StripeScreenshot = {
  src: 'https://cdn.magicpatterns.com/uploads/p1CHhLdjFTWDEQEg2FFMzZ/Case-Studies-18.png',
  alt: 'Stripe dashboard showing 2,034 new customers from January 2020 through January 2026.',
  caption: '2,034 new customers'
};
const CHARCOAL_TEXTURE_URL = 'https://cdn.magicpatterns.com/uploads/scC5pVG3EUePxKUfSG2TRr/Screenshot_2026-07-23_at_10.49.48_AM.png';
export function StatsSection() {
  return <section id="proof" className="w-full bg-[#111415] text-white" style={{
    backgroundImage: `radial-gradient(ellipse 62% 120% at 50% 48%, rgba(21, 47, 74, 0.9) 0%, rgba(15, 28, 41, 0.72) 46%, rgba(17, 20, 21, 0.2) 100%), url('${CHARCOAL_TEXTURE_URL}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }} data-id="element-134" data-mp="1p4jt3rkg4byb">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-id="element-135" data-mp="t46fpp3erd5m">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.45
      }} data-id="element-136" data-mp="173eeigvnsj9a">
          <h2 className="max-w-4xl font-display text-5xl uppercase leading-[0.92] sm:text-6xl lg:text-7xl" data-id="element-137" data-mp="1h1c3x7p98m3h">
            What growth actually looks like.
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/60 sm:text-lg" data-id="element-138" data-mp="1c0ejspkophzc">
            These are real Stripe numbers from a business we work with. Not a projection, not a best case. This is what compounding looks like when the website, ads, and follow-up all pull in the same direction.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6" data-id="element-139" data-mp="1utigkjt78ay6">
          {STATS.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          delay: index * 0.07
        }} className="border-t border-brand-blue pt-4" data-id="element-140" data-mp="2eg3kgm028hkn" data-mpi={index}>
              <div className="font-display text-5xl uppercase leading-none text-brand-blue sm:text-6xl" data-id="element-141" data-mp="29hik8mkt5xw7">{stat.value}</div>
              <p className="mt-3 max-w-[15rem] font-mono-eyebrow text-[10px] font-semibold uppercase tracking-[0.15em] text-white/55" data-id="element-142" data-mp="20fj0vnmbmhq3">
                {stat.label}
              </p>
            </motion.div>)}
        </div>

        <div className="mx-auto mt-16 w-full max-w-5xl space-y-6 lg:mt-20 lg:space-y-8" data-id="element-143" data-mp="1qpzguemvghtn">
          {STRIPE_SCREENSHOTS.map((screenshot, index) => <motion.figure key={screenshot.caption} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.45,
          delay: index * 0.08
        }} className="overflow-hidden rounded-xl border border-white/20 bg-white p-2 shadow-2xl lg:p-3" data-id="element-144" data-mp="1gr0gdjc9fshq" data-mpi={index}>
              <img src={screenshot.src} alt={screenshot.alt} className="h-auto w-full rounded-lg" data-id="element-145" data-mp="uz783yxul23w" />
            </motion.figure>)}

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.45
        }} className="border-t border-white/15 pt-10 text-lg leading-relaxed text-white/70 lg:pt-12" data-id="element-146" data-mp="lyitp34jkzqh">
            <p data-id="element-147" data-mp="2bc4ib5b3wvct">
              Over six years the account climbed from a near standing start to <span className="font-semibold text-white" data-id="element-148" data-mp="26dkvz3slphjx">$13.3M</span> in total gross volume and more than 2,000 new customers. The last two years are where it compounded. Gross volume rose <span className="font-semibold text-white" data-id="element-149" data-mp="2iwouav1bnvre">279%</span> period over period. MRR more than doubled, up <span className="font-semibold text-white" data-id="element-150" data-mp="2nnewy0id5kie">124% to $210K</span>. Active subscribers grew 130% to 495. Successful payments jumped 414%, from 4,142 to 21,306, while spend per customer climbed past $7,000. Slow at first, then steep. That is the curve we build toward.
            </p>
          </motion.div>

          <motion.figure initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.45
        }} className="overflow-hidden rounded-xl border border-white/20 bg-white p-2 shadow-2xl lg:p-3" data-id="element-151" data-mp="1zxauvxktpsti">
            <img src={FINAL_SCREENSHOT.src} alt={FINAL_SCREENSHOT.alt} className="h-auto w-full rounded-lg" data-id="element-152" data-mp="3gbatn57xncf6" />
          </motion.figure>
        </div>
      </div>
    </section>;
}